import { type RcFile } from 'antd/es/upload'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  type UploadFileWithId,
  uploadFileToPresignedUrl,
  waitForImageToBeAvailable,
} from '../model/upload-progress.model'
import { createExtractFileMetadataEffect } from './extract-file-metadata'

export function createSingleUploadModel() {
  const Gate = createGate()

  const extractFileMetadataFx = createExtractFileMetadataEffect()

  const $file = createStore<{ file: UploadFileWithId | null }>({ file: null })

  const fileInit = createEvent<UploadFileWithId | null>()
  const fileAdded = createEvent<RcFile>()
  const fileRemoved = createEvent()
  const fileStartUpload = createEvent<Pick<UploadFileWithId, 'uid' | 'id'>>()
  const fileProgressChanged =
    createEvent<Pick<UploadFileWithId, 'uid' | 'percent'>>()
  const fileUploadSuccess = createEvent<Pick<UploadFileWithId, 'uid' | 'url'>>()
  const fileUploadError = createEvent<Pick<UploadFileWithId, 'uid' | 'error'>>()

  const checkImageAvailabilityFx = createEffect(
    async ({
      file,
      signal,
    }: {
      file: UploadFileWithId | null
      signal: AbortSignal
    }) => {
      if (file?.url) {
        await waitForImageToBeAvailable(file.url, signal)
      }
      return file
    },
  )

  const abortController = createStore<AbortController | null>(null)

  sample({
    clock: Gate.open,
    fn: () => new AbortController(),
    target: abortController,
  })

  sample({
    clock: fileInit,
    source: abortController,
    fn: (controller, file) => {
      if (!controller) throw new Error('AbortController is null')
      return { file, signal: controller.signal }
    },
    target: checkImageAvailabilityFx,
  })

  sample({
    clock: Gate.close,
    source: abortController,
    filter: Boolean,
    fn: (controller) => {
      controller.abort()
    },
    target: fileRemoved,
  })

  $file
    .on(checkImageAvailabilityFx.doneData, (_, file) => ({ file }))
    .on(fileAdded, (_, file) => ({ file }))
    .on(fileRemoved, () => ({ file: null }))
    .on(fileStartUpload, (state, { uid, id }) => {
      if (!state.file || state.file.uid !== uid) return state
      return { file: { ...state.file, status: 'uploading', id } }
    })
    .on(fileProgressChanged, (state, { uid, percent }) => {
      if (!state.file || state.file.uid !== uid) return state
      return { file: { ...state.file, percent } }
    })
    .on(fileUploadSuccess, (state, { uid, url }) => {
      if (!state.file || state.file.uid !== uid) return state
      const newState = { file: state.file }
      newState.file.thumbUrl = url
      newState.file.url = url
      newState.file.status = 'done'
      return newState
    })
    .on(fileUploadError, (state, { uid }) => {
      if (!state.file || state.file.uid !== uid) return state
      return { file: null }
    })

  sample({
    clock: fileAdded,
    target: extractFileMetadataFx,
  })
  sample({
    clock: extractFileMetadataFx.doneData,
    fn: (data) => ({
      ...data,
      fileStartUpload: fileStartUpload,
      fileProgressChanged: fileProgressChanged,
      fileUploadSuccess: fileUploadSuccess,
      fileUploadError: fileUploadError,
    }),
    target: uploadFileToPresignedUrl,
  })

  return {
    Gate,
    $file,
    $fileUploading: $file.map((s) => s.file?.status !== 'done'),
    fileInit,
    fileAdded,
    fileRemoved,
  }
}
